import type { CSSProperties } from 'react';

export type PinProps = {
  /** Pixel offset OR CSS string (e.g. '50%') from the left edge of the parent. */
  left?: number | string;
  top?: number | string;
  right?: number | string;
  bottom?: number | string;
  /** Pin diameter in px. Default 14. */
  size?: number;
  /** Pin head color (CSS color or var). Default ink-red. */
  color?: string;
  className?: string;
};

/**
 * Domed pushpin head — radial gradient + drop shadow, centred on its
 * coordinate (the wrapper translates -50% so `left:'50%'` actually
 * centres). Use it to pin papers / polaroids / postits at any point.
 * Differs from `<Pushpin>` (corner-only) in that it accepts any
 * absolute coordinate; both share the same visual recipe.
 */
export const Pin = ({
  left,
  top,
  right,
  bottom,
  size = 14,
  color = 'var(--color-stamp-red)',
  className,
}: PinProps) => {
  const style: CSSProperties = {
    position: 'absolute',
    width: size,
    height: size,
    borderRadius: '50%',
    background: `radial-gradient(circle at 35% 30%, ${color}, ${color} 50%, rgba(0,0,0,.55) 100%)`,
    boxShadow: '0 2px 4px rgba(0,0,0,.55), inset 0 1px 1px rgba(255,255,255,.45)',
    pointerEvents: 'none',
    zIndex: 30,
  };
  if (left !== undefined) style.left = left;
  if (top !== undefined) style.top = top;
  if (right !== undefined) style.right = right;
  if (bottom !== undefined) style.bottom = bottom;
  // When the pin uses `left: '50%'` (or any string %), translate -50% so
  // the head visually centres on the coordinate.
  if (typeof left === 'string') {
    style.transform = 'translateX(-50%)';
  }
  return <span aria-hidden className={className} style={style} />;
};
